import {inject, Injectable} from '@angular/core';
import {LoginType} from '../../types/login/login.type';
import {firstValueFrom} from 'rxjs';
import {TokenService as ApiTokenService} from '../../api/services/token.service';
import {HttpErrorResponse} from '@angular/common/http';
import {
    OrganisationLicenseExpiredError,
    OrganisationMemberIsInactiveError,
    OrganisationMemberNotFoundError,
    OrganisationNotFoundError,
    ReachedMaximumUsersError,
    TokenAuthenticationError
} from '../../errors/login-errors';
import {AuthStore} from '../../stores/auth.store';
import {AuthQuery} from '../../queries/auth.query';
import {LOGIN_TYPE} from '../../constants/login/login.type.constants';
import {AzureWrongServerError} from '../../errors/azure-wrong-server-error';
import {INVALID_TOKEN} from '../../constants/invalid-token.constant';
import {JwtPayload} from '../../models/token/jwt-payload.model';
import {ApteraApiError} from '../../models/aptera-api-error.model';
import {AuthenticationContext} from '../../api/models/authentication-context';
import {ApiErrorCodeEnum} from '../../enums/api-error-code.enum';
import {TokenExpireData} from '../../models/token/token-expire-data.model';

@Injectable({
    providedIn: 'root'
})
export class TokenService {
    private readonly apiTokenService = inject(ApiTokenService);
    private readonly authStore = inject(AuthStore);
    private readonly authQuery = inject(AuthQuery);
    private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;
    private jwt: string | undefined;
    private jwtPayload: JwtPayload | undefined;

    /**
     * Generates a token by authenticating the user with the provided credentials and save the JWT in the store.
     *
     * @param {LoginType} loginType - The type of login being used, indicating the authentication method.
     * @param {string} username - The username of the user attempting to authenticate.
     * @param {string} password - The password of the user attempting to authenticate.
     * @param {AuthenticationContext} authenticationContext - Optional context for organisation
     * @throws {TokenAuthenticationError} - Error that occurs when the user could not be authenticated (invalid credentials or user is inactive).
     * @throws {OrganisationNotFoundError} - Error that occurs when the organisation the user tried to log in to does not exist.
     * @throws {OrganisationMemberNotFoundError} - Error that occurs when the user is not a member of the organisation he tries to log in to.
     * @throws {OrganisationMemberIsInactiveError} - Error that occurs when the user has been set to inactive within the organisation.
     * @throws {Error} - Error that occurs, while processing the token
     */
    public async createToken(loginType: LoginType, username: string, password: string, authenticationContext?: AuthenticationContext): Promise<void> {
        try {
            this.invalidateToken();

            if (!authenticationContext) {
                const loggedInOrganisationId = this.authQuery.getOrganisationId();
                authenticationContext = {
                    authenticationTarget: loggedInOrganisationId ? 'SpecificOrganisation' : 'PreferredOrganisation',
                    specificOrganisation: loggedInOrganisationId || undefined,
                };
            }

            this.jwt = this.getJwtWithoutQuotes(await firstValueFrom(this.apiTokenService.TokenCreate({
                authenticationType: loginType,
                userName: username,
                password,
                authenticationContext
            })));

            this.jwtPayload = this.getTokenPayload<JwtPayload>(this.jwt);

            let localExpireTimestamp = 0;
            if (this.jwtPayload) {
                localExpireTimestamp = this.getExpireTimestampFromJwtPayload(this.jwtPayload);
            }

            this.authStore.update({
                token: this.jwt,
                tokenExpiresTs: localExpireTimestamp,
            });
        } catch (error) {
            this.throwTokenCreationError(error, loginType);
        }
    }

    public invalidateToken(tokenExpireData?: TokenExpireData): void {
        this.authStore.update(tokenExpireData ?? {
            token: INVALID_TOKEN,
            tokenExpiresTs: 0
        });
    }

    public isCurrentTokenValid(): boolean {
        const token = this.authQuery.getToken();
        if (!token || token === INVALID_TOKEN) {
            return false;
        }
        return Date.now() < this.authQuery.getTokenExpiresTs();
    }

    public hasToken(): boolean {
        return this.authQuery.hasToken();
    }

    public scheduleTokenRefresh(callbackFunction: () => Promise<unknown>, bufferTimeBeforeRefreshInMs: number = 60000): void {
        this.clearTokenRefreshTimer();

        try {
            const jwtInStoreExpiresTimestamp = this.authQuery.getTokenExpiresTs();
            if (jwtInStoreExpiresTimestamp > 0) {
                this.tokenRefreshTimer = setTimeout(async () => {
                    await callbackFunction();
                }, jwtInStoreExpiresTimestamp - Date.now() - bufferTimeBeforeRefreshInMs);
            }
        } catch (error) {
            this.clearTokenRefreshTimer();

            throw error;
        }
    }

    public clearTokenRefreshTimer(): void {
        if (this.tokenRefreshTimer !== null) {
            clearTimeout(this.tokenRefreshTimer);
        }
    }

    public getUserIdFromTokenPayload(): string | undefined {
        if (!this.authQuery.hasToken()) {
            throw new Error('Token not found. Please create a new token before getting user id from token payload.');
        }

        return this.jwtPayload?.UserId;
    }

    public getOrganisationIdFromTokenPayload(): string | undefined {
        if (!this.authQuery.hasToken()) {
            throw new Error('Token not found. Please create a new token before getting organisation id from token payload.');
        }

        return this.jwtPayload?.OrganisationId;
    }

    public getToken(): string {
        if (!this.isCurrentTokenValid()) {
            throw new Error('Token not found or not valid. Please create a new token.');
        }

        return this.authQuery.getToken();
    }

    private throwTokenCreationError(error: unknown, loginType: LoginType): void {
        if (!(error instanceof HttpErrorResponse)) {
            throw error;
        }

        if (typeof error.error === 'string') {
            let errorObj: ApteraApiError | undefined;
            try {
                errorObj = JSON.parse(error.error) as ApteraApiError;
            } catch {
            }

            if (errorObj?.code) {
                switch (errorObj.code) {
                    case ApiErrorCodeEnum.OrganisationLicenseExpired:
                        throw new OrganisationLicenseExpiredError();
                    case ApiErrorCodeEnum.ReachedMaximumUsers:
                        throw new ReachedMaximumUsersError();
                    case ApiErrorCodeEnum.OrganisationNotFound:
                        throw new OrganisationNotFoundError();
                    case ApiErrorCodeEnum.OrganisationMemberNotFound:
                        throw new OrganisationMemberNotFoundError();
                    case ApiErrorCodeEnum.InactiveOrganisationMember:
                        throw new OrganisationMemberIsInactiveError();
                }
            }
        }

        if (error.status === 401) {
            if (loginType === LOGIN_TYPE.AZURE) {
                throw new AzureWrongServerError();
            }

            throw new TokenAuthenticationError(error.status, error.message);
        }

        throw error;
    }

    private getTokenPayload<T>(token: string): T | undefined {
        let jwtPayload: T | undefined;
        try {
            const jwtSections = token.split('.');
            jwtPayload = JSON.parse(atob(jwtSections[1]));
        } catch (e) {
            console.error(e);
        }
        return jwtPayload;
    }

    private getExpireTimestampFromJwtPayload(jwtPayload: JwtPayload): number {
        // We need to calculate a local expire time for devices with wrong time settings
        const expireIntervalInSeconds = Number.parseInt(jwtPayload.exp, 10) - Number.parseInt(jwtPayload.nbf, 10);
        return Date.now() + (expireIntervalInSeconds * 1000);
    }

    private getJwtWithoutQuotes(jwt: string): string {
        return jwt.replace(/^"|"$/g, '');
    }
}
